import React, { useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { useAppSelector, useAppDispatch } from "../../../hooks/hooks";
import { openGlobalPopup, closeGlobalPopup } from "../globalPopupSlice";
import Icon from "../../../components/Icon/Icon";

const SESSION_STORAGE_KEY = "has_seen_global_popup";

const GlobalPopup: React.FC = () => {
  const dispatch = useAppDispatch();
  const { isOpen, imageUrl, linkUrl } = useAppSelector(
    (state) => state.globalPopup,
  );
  const portalRoot = document.body;
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const settings = window.commonVariables?.popupSettings;
    if (!settings || !settings.image) return;

    const hasSeen = sessionStorage.getItem(SESSION_STORAGE_KEY);

    // Evaluate if we should open:
    // True if behavior is set to 'every_visit', OR if they haven't seen it yet.
    const shouldShow = settings.behavior === "every_visit" || !hasSeen;

    if (shouldShow) {
      dispatch(
        openGlobalPopup({
          imageUrl: settings.image,
          linkUrl: settings.link || null,
        }),
      );

      // --- CRITICAL BUG FIX: Set the session key the moment it is SHOWN ---
      // This ensures that even if the user refreshes the page immediately,
      // or clicks the banner link to navigate to another page, the session
      // flag is already securely saved.
      if (settings.behavior === "once_per_session") {
        sessionStorage.setItem(SESSION_STORAGE_KEY, "true");
      }
    }
  }, [dispatch]);

  const handleClose = () => {
    dispatch(closeGlobalPopup());
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") handleClose();
    };

    if (isOpen) {
      document.body.classList.add("global-popup-open");
      document.addEventListener("keydown", handleKeyDown);
    } else {
      document.body.classList.remove("global-popup-open");
    }

    return () => {
      document.body.classList.remove("global-popup-open");
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  // Click outside to close logic
  const handleOverlayClick = (e: React.MouseEvent) => {
    if (contentRef.current && !contentRef.current.contains(e.target as Node)) {
      handleClose();
    }
  };

  if (!isOpen || !portalRoot || !imageUrl) {
    return null;
  }

  return ReactDOM.createPortal(
    <div
      className={`global-popup-wrapper ${isOpen ? "is-open" : ""}`}
      aria-hidden={!isOpen}
    >
      <div className="global-popup__overlay" onClick={handleOverlayClick} />
      <div
        ref={contentRef}
        className="global-popup__content"
        role="dialog"
        aria-modal="true"
      >
        <button
          onClick={handleClose}
          className="global-popup__close-btn"
          aria-label="Close popup"
        >
          <Icon iconName="x-mark" className="icon--stroke-thickest" />
        </button>

        {/* --- DYNAMIC LINK WRAPPER --- */}
        {linkUrl ? (
          <a
            href={linkUrl}
            className="global-popup__link"
            style={{ display: "block", outline: "none" }}
          >
            <img src={imageUrl} alt="Announcement" />
          </a>
        ) : (
          <img src={imageUrl} alt="Announcement" />
        )}
      </div>
    </div>,
    portalRoot,
  );
};

export default GlobalPopup;
